import {NewsItemService} from "../../service/items/NewsItemService";
import {Observable, Subscriber} from "rxjs";
import {NewsHeadlineRecord} from "../../models/NewsHeadlineRecord";
import {OffsetDateTime} from "../../../pseudo-types/DateTypes";
import moment from "moment";
import {CodeAndExchange, Securities, securityEquality} from "../../../instruments/types/CodeAndExchange";
import {Predicate} from "../../../types/GenericTypes";
import {ArrayUtil} from "../../../js/util/ArrayUtil";
import {DateTimeUtil} from "../../../js/util/DateTimeUtil";
import {CurrentUserService} from "../../../services/user/CurrentUserService";
import {FeatureNames} from "../../../types/UserConfig";
import {identity} from "../../../js/util/UtilFunctions";

const UpdateIntervalSeconds = 60;
const UpdateInterval = UpdateIntervalSeconds * 1000;

export class NewsHeadlineStreamStore {

    public static readonly id = "newsHeadlineStreamStore";

    private readonly updates$: Observable<NewsHeadlineRecord>;

    /** @ngInject */
    public constructor(private readonly newsItemService: NewsItemService,
                       private readonly currentUserService: CurrentUserService) {
        this.updates$ = this.buildUpdates()
                            .publishReplay(1)
                            .refCount();
    }

    private buildUpdates(): Observable<NewsHeadlineRecord> {
        return this.currentUserService
                   .current
                   .map((u) => u.isLoggedIn && u.hasFeature(FeatureNames.NEWS))
                   .distinctUntilChanged()
                   .switchMap((shouldUpdate) => this.headlineUpdatesSource(shouldUpdate));
    }

    private headlineUpdatesSource(shouldUpdate: boolean): Observable<NewsHeadlineRecord> {
        if (shouldUpdate) {
            return this.pollingUpdateSource();
        } else {
            return this.emptyUpdateSource();
        }
    }

    private emptyUpdateSource(): Observable<NewsHeadlineRecord> {
        return Observable.empty();
    }

    private pollingUpdateSource(): Observable<NewsHeadlineRecord> {
        return Observable.create((subscriber: Subscriber<NewsHeadlineRecord>) => {
            let lastCheck = this.startTime();

            let check: Function;

            check = async () => {
                try {
                    if (subscriber.closed) {
                        return;
                    }
                    const updates = await this.newsItemService.headlinesSince(lastCheck);
                    if (updates.length !== 0) {
                        lastCheck = [...updates].map((h) => h.at).sort().reverse()[0];
                    }
                    if (!subscriber.closed) {
                        updates.forEach((h) => subscriber.next(h));
                    }
                } catch (e) {
                    console.error("Unable to retrieve news headline updates", e);
                } finally {
                    if (!subscriber.closed) {
                        setTimeout(() => check(), UpdateInterval);
                    }
                }
            };

            check();
        });
    }

    private startTime(): OffsetDateTime {
        const now = moment();
        const start = now.subtract(5, "minutes");
        return DateTimeUtil.offsetDateTime(start);
    }

    public headlines(codeOrCodes?: CodeAndExchange | Securities): Observable<NewsHeadlineRecord> {
        if (codeOrCodes == null) {
            return this.updates$;
        } else {
            const filter = this.filter(codeOrCodes);
            return this.updates$.filter(filter);
        }
    }

    private filter(codeOrCodes: CodeAndExchange | Securities): Predicate<NewsHeadlineRecord> {
        if (ArrayUtil.isArray(codeOrCodes)) {
            return (headline) => codeOrCodes.some(securityEquality(headline));
        } else {
            return securityEquality(codeOrCodes);
        }
    }
}
